import { Sun, Cloud, CloudRain, CloudLightning, CloudFog, Snowflake } from 'lucide-react';

type WmoEntry = {
  pt: string;
  en: string;
  es: string;
  de: string;
  fr: string;
  iconType: 'sun' | 'cloud' | 'rain' | 'fog' | 'snow' | 'lightning';
};

const WMO_DATA: Record<number, WmoEntry> = {
  0: {
    pt: 'Céu limpo',
    en: 'Clear sky',
    es: 'Cielo despejado',
    de: 'Klarer Himmel',
    fr: 'Ciel dégagé',
    iconType: 'sun',
  },
  1: {
    pt: 'Principalmente limpo',
    en: 'Mainly clear',
    es: 'Principalmente despejado',
    de: 'Überwiegend klar',
    fr: 'Principalement dégagé',
    iconType: 'sun',
  },
  2: {
    pt: 'Parcialmente nublado',
    en: 'Partly cloudy',
    es: 'Parcialmente nublado',
    de: 'Teilweise bewölkt',
    fr: 'Partiellement nuageux',
    iconType: 'cloud',
  },
  3: {
    pt: 'Nublado',
    en: 'Overcast',
    es: 'Nublado',
    de: 'Bedeckt',
    fr: 'Couvert',
    iconType: 'cloud',
  },
  45: { pt: 'Nevoeiro', en: 'Fog', es: 'Niebla', de: 'Nebel', fr: 'Brouillard', iconType: 'fog' },
  48: {
    pt: 'Nevoeiro com geada',
    en: 'Depositing rime fog',
    es: 'Niebla con escarcha',
    de: 'Raureifnebel',
    fr: 'Brouillard givrant',
    iconType: 'fog',
  },
  51: {
    pt: 'Garoa leve',
    en: 'Light drizzle',
    es: 'Llovizna ligera',
    de: 'Leichter Nieselregen',
    fr: 'Bruine légère',
    iconType: 'rain',
  },
  53: {
    pt: 'Garoa moderada',
    en: 'Moderate drizzle',
    es: 'Llovizna moderada',
    de: 'Mäßiger Nieselregen',
    fr: 'Bruine modérée',
    iconType: 'rain',
  },
  55: {
    pt: 'Garoa intensa',
    en: 'Dense drizzle',
    es: 'Llovizna densa',
    de: 'Dichter Nieselregen',
    fr: 'Bruine dense',
    iconType: 'rain',
  },
  61: {
    pt: 'Chuva leve',
    en: 'Slight rain',
    es: 'Lluvia ligera',
    de: 'Leichter Regen',
    fr: 'Pluie légère',
    iconType: 'rain',
  },
  63: {
    pt: 'Chuva moderada',
    en: 'Moderate rain',
    es: 'Lluvia moderada',
    de: 'Mäßiger Regen',
    fr: 'Pluie modérée',
    iconType: 'rain',
  },
  65: {
    pt: 'Chuva forte',
    en: 'Heavy rain',
    es: 'Lluvia intensa',
    de: 'Starker Regen',
    fr: 'Forte pluie',
    iconType: 'rain',
  },
  71: {
    pt: 'Neve leve',
    en: 'Slight snow',
    es: 'Nieve ligera',
    de: 'Leichter Schneefall',
    fr: 'Neige légère',
    iconType: 'snow',
  },
  73: {
    pt: 'Neve moderada',
    en: 'Moderate snow',
    es: 'Nieve moderada',
    de: 'Mäßiger Schneefall',
    fr: 'Neige modérée',
    iconType: 'snow',
  },
  75: {
    pt: 'Neve intensa',
    en: 'Heavy snow',
    es: 'Nieve intensa',
    de: 'Starker Schneefall',
    fr: 'Neige intense',
    iconType: 'snow',
  },
  80: {
    pt: 'Pancadas leves',
    en: 'Slight rain showers',
    es: 'Chubascos ligeros',
    de: 'Leichte Regenschauer',
    fr: 'Averses légères',
    iconType: 'rain',
  },
  81: {
    pt: 'Pancadas moderadas',
    en: 'Moderate rain showers',
    es: 'Chubascos moderados',
    de: 'Mäßige Regenschauer',
    fr: 'Averses modérées',
    iconType: 'rain',
  },
  82: {
    pt: 'Pancadas violentas',
    en: 'Violent rain showers',
    es: 'Chubascos violentos',
    de: 'Heftige Regenschauer',
    fr: 'Averses violentes',
    iconType: 'rain',
  },
  95: {
    pt: 'Tempestade',
    en: 'Thunderstorm',
    es: 'Tormenta',
    de: 'Gewitter',
    fr: 'Orage',
    iconType: 'lightning',
  },
  96: {
    pt: 'Tempestade com granizo',
    en: 'Thunderstorm with hail',
    es: 'Tormenta con granizo',
    de: 'Gewitter mit Hagel',
    fr: 'Orage avec grêle',
    iconType: 'lightning',
  },
  99: {
    pt: 'Tempestade forte com granizo',
    en: 'Heavy thunderstorm with hail',
    es: 'Tormenta fuerte con granizo',
    de: 'Schweres Gewitter mit Hagel',
    fr: 'Orage fort avec grêle',
    iconType: 'lightning',
  },
};

export function getWmoLabel(code: number, lang: string): string {
  const entry = WMO_DATA[code] ?? WMO_DATA[0];
  switch (lang) {
    case 'en':
      return entry.en;
    case 'es':
      return entry.es;
    case 'de':
      return entry.de;
    case 'fr':
      return entry.fr;
    default:
      return entry.pt;
  }
}

export function getWmoIcon(code: number, className: string): React.ReactNode {
  const entry = WMO_DATA[code] ?? WMO_DATA[0];
  switch (entry.iconType) {
    case 'sun':
      return <Sun className={className} />;
    case 'cloud':
      return <Cloud className={className} />;
    case 'rain':
      return <CloudRain className={className} />;
    case 'fog':
      return <CloudFog className={className} />;
    case 'snow':
      return <Snowflake className={className} />;
    case 'lightning':
      return <CloudLightning className={className} />;
  }
}
